import keyboard from 'keyboard-key';
import { viewport } from './Viewport';

const NAME_SPACE = 'jumpmenu';

class JumpMenu {
  constructor() {
    this.links = [];
    this.element = null;
    this.currentIndex = 0;
  }

  init(element) {
    // Assign this element to the element passed.
    this.element = element;

    // if an element wasn't passed, stop.
    if (!this.element || !this.links.length) return;

    // Stop if links haven't been added to the array.
    if (!this.links.length) return;

    // If a list and a button toggle aren't found, stop.
    if (!this.list || !this.toggle) return;

    // Continue...
    this.build();
    this.listen();
    this.ready();
  }

  build() {
    // Assign attributes to the toggle button.
    this.toggle.classList.add(`${NAME_SPACE}__toggle`);
    this.toggle.setAttribute('aria-haspopup', true);
    this.toggle.setAttribute('aria-expanded', true);

    // Assign attributes to the list.
    this.list.classList.add(`${NAME_SPACE}__list`);
    this.list.setAttribute('role', 'menu');

    // Create list items with links
    this.links.forEach((obj) => {
      const item = document.createElement('li');
      const link = document.createElement('a');
      if ('actualLink' in obj) {
        link.setAttribute('href', `${obj.actualLink}`);
      } else {
        link.setAttribute('href', `#${obj.id}`);
      }
      link.setAttribute('role', 'menuitem');
      link.setAttribute('aria-label', `Skip to ${obj.label} Section`);
      link.textContent = obj.label;
      this.list.appendChild(item);
      item.appendChild(link);
    });
  }

  listen() {
    this.element.addEventListener('focusout', this.focusOutHandler.bind(this));
    this.element.addEventListener('keydown', this.keydownHandler.bind(this));
    this.toggle.addEventListener('click', this.clickHandler.bind(this));
    this.list.addEventListener('click', this.clickHandler.bind(this));
    viewport.onDesktopChange(this.desktopStateHandler.bind(this));
    viewport.onMobileChange(this.mobileStateHandler.bind(this));
  }

  ready() {
    this.element.classList.add('ready');
  }

  move(event) {
    event.preventDefault();
    return this;
  }

  addJumpLink(obj) {
    this.links.push(obj);
  }

  focusOutHandler(event) {
    if (!this.element.contains(event.relatedTarget)) {
      setTimeout(() => {
        this.close();
      }, 500);
    }
  }

  keydownHandler(event) {
    const pressed = keyboard.getCode(event);
    const next = viewport.mobileState ? keyboard.ArrowDown : keyboard.ArrowRight;
    const prev = viewport.mobileState ? keyboard.ArrowUp : keyboard.ArrowLeft;

    switch (pressed) {
      case next:
        event.preventDefault();
        this.updateSelection(this.currentIndex + 1);
        break;
      case prev:
        event.preventDefault();
        this.updateSelection(this.currentIndex - 1);
        break;
      case keyboard.Escape:
        this.close();
        break;
      default: break;
    }
  }

  updateSelection(index) {
    // Set currentIndex based on passed in value.
    this.index = index;

    // Focus active element based on currentIndex.
    this.options[this.currentIndex].focus();
  }

  clickHandler(event) {
    // Prevent default behavior of interactive components.
    if (event.target.href.indexOf(window.location.href) > -1) {
      event.preventDefault();

      // Get reference to the currently expanded state.
      const expanded = this.toggle.getAttribute('aria-expanded') === 'true';

      // Alter expanded state based on aria attribute currently set.
      this.toggle.setAttribute('aria-expanded', !expanded);

      // If we're expanding the menu, focus the first link.
      if (this.toggle === event.currentTarget && !expanded) {
        this.updateSelection(0);
      }
    }
  }

  mobileStateHandler() {
    this.list.setAttribute('aria-orientation', 'vertical');
    this.close();
  }

  desktopStateHandler() {
    this.list.setAttribute('aria-orientation', 'horizontal');
    this.open();
  }

  open() {
    this.toggle.setAttribute('aria-expanded', true);
  }

  close() {
    this.toggle.setAttribute('aria-expanded', false);
  }

  set index(value) {
    if (value >= this.total) {
      this.currentIndex = 0;
    } else if (value < 0) {
      this.currentIndex = this.total - 1;
    } else {
      this.currentIndex = value;
    }
  }

  get index() {
    return this.currentIndex;
  }

  get total() {
    return this.options.length;
  }

  get toggle() {
    return this.element.querySelector('button');
  }

  get list() {
    return this.element.querySelector('ul');
  }

  get options() {
    return this.list.querySelectorAll('a');
  }
}

export default new JumpMenu();
